import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { buildings } from '../../data/buildings';
import { technologies } from '../../data/technologies';
import { resources } from '../../data/resources';
import { useGame } from '../../context/GameContext';

const GAP = '6px';

/**
 * BuildingChip - Displays buildings with rich tooltips
 */
function BuildingChip({ 
  id, 
  count,
  showIcon = true,
  showName = true,
  className = '',
  size = 'normal',
  fullWidth = false,
}) {
  const [tooltip, setTooltip] = useState(null);
  const { researchedTechs } = useGame();

  const building = buildings[id];
  
  if (!building) {
    return <span className="bc-unknown">❓{id}</span>;
  }

  const requiredTech = building.unlockCondition?.tech;
  const isUnlocked = !requiredTech || researchedTechs.includes(requiredTech);

  const handleMouseEnter = (e) => setTooltip({ x: e.clientX, y: e.clientY });
  const handleMouseMove = (e) => { if (tooltip) setTooltip({ x: e.clientX, y: e.clientY }); };
  const handleMouseLeave = () => setTooltip(null);

  const sizeClass = size === 'small' ? 'bc-small' : size === 'large' ? 'bc-large' : '';
  
  const chipStyle = {
    backgroundColor: isUnlocked ? 'rgba(72, 187, 120, 0.15)' : 'rgba(252, 129, 129, 0.15)',
    borderLeft: isUnlocked ? '3px solid rgba(72, 187, 120, 0.6)' : '3px solid rgba(252, 129, 129, 0.6)',
    ...(fullWidth && { display: 'flex', width: '100%', justifyContent: 'flex-start' }),
  };

  return (
    <>
      <span
        className={`building-chip ${sizeClass} ${className}`}
        style={chipStyle}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {showIcon && <span className="bc-icon">{building.icon}</span>}
        {showName && <span className="bc-name">{building.name}</span>}
        {count !== undefined && <span className="bc-count">×{count}</span>}
      </span>

      {tooltip && ReactDOM.createPortal(
        <BuildingTooltip building={building} x={tooltip.x} y={tooltip.y} isUnlocked={isUnlocked} />,
        document.body
      )}
    </>
  );
}

/**
 * BuildingTooltip - Clean, minimal layout
 */
function BuildingTooltip({ building, x, y, isUnlocked }) {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ left: x + 15, top: y - 10 });
  const tooltipWidth = 300;

  useEffect(() => {
    const margin = 10;
    let left = x + 15;
    let top = y - 10;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 400;
    
    if (left + tooltipWidth > window.innerWidth - margin) left = x - tooltipWidth - 15;
    if (left < margin) left = margin;
    if (top + tooltipHeight > window.innerHeight - margin) top = window.innerHeight - tooltipHeight - margin;
    if (top < margin) top = margin;
    
    setPosition({ left, top });
  }, [x, y]);

  const requiredTechId = building.unlockCondition?.tech;
  const requiredTech = requiredTechId ? technologies[requiredTechId] : null;
  const techChain = requiredTech ? getTechChain(requiredTechId) : [];

  const production = building.baseProduction || {};
  const hasProduction = Object.keys(production).length > 0;
  const cost = building.baseCost || {};

  // Status colors - the important ones
  const successColor = '#68d391';
  const successBg = 'rgba(72, 187, 120, 0.15)';
  const dangerColor = '#fc8181';
  const dangerBg = 'rgba(252, 129, 129, 0.15)';
  
  // Muted accent colors
  const mutedText = 'var(--text-secondary, #888)';
  const dimText = 'var(--text-secondary, #666)';

  const statusColor = isUnlocked ? successColor : dangerColor;
  const statusBg = isUnlocked ? successBg : dangerBg;
  const borderColor = isUnlocked ? 'rgba(72, 187, 120, 0.4)' : 'rgba(252, 129, 129, 0.4)';

  // Font sizes: title, body, label
  const fontTitle = '0.85rem';
  const fontBody = '0.7rem';
  const fontLabel = '0.55rem';

  return (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        zIndex: 10000,
        left: position.left,
        top: position.top,
        width: tooltipWidth,
        backgroundColor: 'var(--bg-secondary, #1e1e2f)',
        borderRadius: '6px',
        border: `1px solid ${borderColor}`,
        boxShadow: isUnlocked 
          ? '0 0 20px rgba(72, 187, 120, 0.15), 0 8px 32px rgba(0, 0, 0, 0.4)' 
          : '0 0 20px rgba(252, 129, 129, 0.2), 0 8px 32px rgba(0, 0, 0, 0.4)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        background: `linear-gradient(135deg, ${statusBg} 0%, transparent 100%)`,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ fontSize: '1.5rem' }}>{building.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: fontTitle, fontWeight: 600, color: 'var(--text-primary, #fff)' }}>
            {building.name}
          </div>
          <div style={{ fontSize: fontLabel, color: mutedText }}>Tier {building.tier}</div>
        </div>
        <div style={{
          fontSize: fontLabel,
          padding: '2px 6px',
          color: statusColor,
          fontWeight: 600,
        }}>
          {isUnlocked ? '✓ Unlocked' : 'Locked'}
        </div>
      </div>

      {/* BODY */}
      <div style={{ padding: '12px', display: 'grid', gap: '12px' }}>
        
        {/* Description */}
        <div style={{ fontSize: fontBody, color: 'var(--text-primary, #ccc)', lineHeight: 1.5 }}>
          {building.description}
        </div>

        {/* Tech Requirement */}
        {requiredTech ? (
          <div style={{
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${isUnlocked ? 'rgba(72, 187, 120, 0.4)' : 'rgba(252, 129, 129, 0.4)'}`,
            background: isUnlocked ? successBg : dangerBg,
            boxShadow: isUnlocked 
              ? '0 0 12px rgba(72, 187, 120, 0.2), inset 0 0 8px rgba(72, 187, 120, 0.08)' 
              : '0 0 12px rgba(252, 129, 129, 0.2), inset 0 0 8px rgba(252, 129, 129, 0.08)',
          }}>
            <div style={{ fontSize: fontLabel, color: statusColor, marginBottom: '8px', fontWeight: 600 }}>
              Requires Technology
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.1rem' }}>{requiredTech.icon}</span>
              <span style={{ fontSize: fontBody, fontWeight: 500, color: 'var(--text-primary, #fff)' }}>
                {requiredTech.name}
              </span>
              <span style={{ fontSize: fontLabel, color: dimText, marginLeft: 'auto' }}>T{requiredTech.tier}</span>
            </div>
            {techChain.length > 0 && (
              <div style={{ fontSize: fontLabel, color: dimText, marginTop: '8px' }}>
                Path: {techChain.map(t => t.name).join(' → ')} → {requiredTech.name}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            padding: '8px 10px',
            borderRadius: '4px',
            border: '1px solid rgba(72, 187, 120, 0.4)',
            background: successBg,
            boxShadow: '0 0 12px rgba(72, 187, 120, 0.15), inset 0 0 8px rgba(72, 187, 120, 0.05)',
            fontSize: fontLabel,
            color: successColor,
            fontWeight: 600,
          }}>
            Available from start
          </div>
        )}

        {/* Cost */}
        <div>
          <div style={{ fontSize: fontLabel, color: dimText, marginBottom: '6px' }}>Base Cost</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(cost).map(([resId, amount]) => {
              const res = resources[resId];
              return (
                <span key={resId} style={{ 
                  fontSize: fontBody, 
                  color: 'var(--text-primary, #ddd)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <span>{res?.icon || '❓'}</span>
                  <span>{amount >= 1000 ? `${(amount/1000).toFixed(1)}k` : amount}</span>
                  <span style={{ color: dimText }}>{res?.name || resId}</span>
                </span>
              );
            })}
          </div>
          <div style={{ fontSize: fontLabel, color: dimText, marginTop: '4px' }}>+15% cost per owned</div>
        </div>

        {/* Production */}
        {hasProduction && (
          <div>
            <div style={{ fontSize: fontLabel, color: dimText, marginBottom: '6px' }}>Produces</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(production).map(([resId, rate]) => {
                const res = resources[resId];
                return (
                  <span key={resId} style={{ 
                    fontSize: fontBody, 
                    color: successColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <span>{res?.icon || '❓'}</span>
                    <span>+{rate}/s</span>
                    <span style={{ color: dimText }}>{res?.name || resId}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Flavor */}
      {building.flavor && (
        <div style={{
          padding: '10px 12px',
          fontSize: fontLabel,
          color: dimText,
          fontStyle: 'italic',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          textAlign: 'center',
        }}>
          "{building.flavor}"
        </div>
      )}
    </div>
  );
}

/**
 * Get the prerequisite chain for a technology
 */
function getTechChain(techId, maxDepth = 3) {
  const chain = [];
  const visited = new Set();
  
  function traverse(id, depth) {
    if (depth > maxDepth || visited.has(id)) return;
    visited.add(id);
    
    const tech = technologies[id];
    if (!tech || !tech.requires) return;
    
    for (const prereqId of tech.requires) {
      const prereq = technologies[prereqId];
      if (prereq && !visited.has(prereqId)) {
        traverse(prereqId, depth + 1);
        chain.push(prereq);
      }
    }
  }
  
  traverse(techId, 0);
  return chain.slice(0, 5);
}

export default BuildingChip;
